import React from 'react';
import { Upload, message, Button } from 'antd';
import ArrowUpIcon from "../../../assets/images/icons/arrow_up.svg";
import { CloseRounded } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { toggleTour } from '../../../reducers/tourSlice';
import * as XLSX from "xlsx";

const { Dragger } = Upload;

const UploadMedia = ({ handleFileSelect, fileList, handelClick, disabled }) => {
    const dispatch = useDispatch();

    const beforeUpload = (file) => {
        const allowedMimeType =
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

        if (file.type !== allowedMimeType) {
            message.error("Invalid File format. File should be in .xlsx format");
            handleFileSelect([]);
            return Upload.LIST_IGNORE;
        }

        const validFilenameRegex = /^[a-zA-Z0-9\-&_ ]+\.xlsx$/;
        if (!validFilenameRegex.test(file.name)) {
            message.error("File name contains invalid characters.");
            handleFileSelect([]);
            return Upload.LIST_IGNORE;
        }

        if (file.size / 1024 / 1024 > 20) {
            message.error("File must be smaller than 20 MB");
            handleFileSelect([]);
            return Upload.LIST_IGNORE;
        }

        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = e.target.result;

                /* ---------------- MAGIC NUMBER CHECK ---------------- */
                const fileBytes = new Uint8Array(data.slice(0, 4));
                const magicNumber = [80, 75, 3, 4];

                const isXlsxFile = fileBytes.every(
                    (byte, index) => byte === magicNumber[index]
                );

                if (!isXlsxFile) {
                    message.error(
                        "Invalid File format. File should be a valid .xlsx file."
                    );
                    handleFileSelect([]);
                    return Upload.LIST_IGNORE;
                }

                /* ---------------- READ EXCEL ---------------- */
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];

                // 1. Get ALL rows as an array of arrays
                const rows = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    blankrows: false,
                });

                if (!rows || rows.length === 0) {
                    message.error("Excel file is empty.");
                    handleFileSelect([]);
                    return Upload.LIST_IGNORE;
                }

                // 2. Extract the header (first row)
                const header = rows[0];

                /* ---------------- HEADER VALIDATION ---------------- */
                const expectedHeader = ["Policy_Number"];
                const normalizedHeader = header.map((h) => String(h).trim());

                const isValidHeader =
                    normalizedHeader.length === 1 &&
                    normalizedHeader[0] === expectedHeader[0];

                if (!isValidHeader) {
                    message.error("Excel must contain only one header: Policy_Number");
                    handleFileSelect([]);
                    return Upload.LIST_IGNORE;
                }

                /* ---------------- POLICY NUMBER VALIDATION ---------------- */
                const dataRows = rows.slice(1);

                // Optional check: Ensure there is actually data below the header
                if (dataRows.length === 0) {
                    message.error("Excel contains a header but no policy numbers.");
                    handleFileSelect([]);
                    return Upload.LIST_IGNORE;
                }

                // Check for empty policy number cells
                const hasEmptyPolicyNumber = dataRows.some((row) => {
                    return String(row[0] ?? "").trim() === "";
                });

                if (hasEmptyPolicyNumber) {
                    message.error("Excel should not contain empty Policy_Number values.");
                    handleFileSelect([]);
                    return Upload.LIST_IGNORE;
                }

                // Maximum 500 policy numbers allowed
                if (dataRows.length > 500) {
                    message.error("Excel can contain a maximum of 500 policy numbers.");
                    handleFileSelect([]);
                    return Upload.LIST_IGNORE;
                }

                const hasInvalidPolicyNumber = dataRows.some((row) => {
                    // row[0] targets the first column (Policy_Number)
                    const policyNumber = String(row[0] ?? "").trim();

                    // Rejects if empty OR contains anything other than digits
                    return !/^\d+$/.test(policyNumber);
                });

                if (hasInvalidPolicyNumber) {
                    message.error("Policy_Number must contain only numeric values.");
                    handleFileSelect([]);
                    return Upload.LIST_IGNORE;
                }

                /* ---------------- SUCCESS ---------------- */
                handleFileSelect([file]);

            } catch (error) {
                message.error("Failed to read Excel file.");
                handleFileSelect([]);
            }
        };

        reader.readAsArrayBuffer(file);

        return Upload.LIST_IGNORE;
    };
    const handleFileChange = (file) => {
        const data = file.fileList.map(item => item.originFileObj ? item.originFileObj : item)
        handleFileSelect(data);
    };

    const customRequest = ({ file, onSuccess }) => {
        if (file && file.name) {
            onSuccess("ok");
        }
    }

    const onRemove = () => {
        handleFileSelect([]);
    }

    const handleClickOnDragger = () => {
        dispatch(toggleTour({ show: false }));
    }

    return (
        <Dragger className='pcvc-upload'
            fileList={fileList}
            multiple={false}
            beforeUpload={beforeUpload}
            onChange={handleFileChange}
            customRequest={customRequest}
            showUploadList={false}
            maxCount={1}
            onClick={handleClickOnDragger}
            disabled={fileList.length > 0}
        >
            <div className='upload-container'>
                {
                    fileList.length === 0 ?
                        <div className='upload-action'>
                            <div className='title'>Drag and drop here or</div>
                            <button className="btn-outlined">
                                <div className='btn-outlined-content'>
                                    <div className='text'>Upload documents </div>
                                    <img width={29} src={ArrowUpIcon} />
                                </div>
                            </button>
                        </div>
                        :
                        <div className="upload-action" style={{ zIndex: 9999 }} onClick={() => { return; }}>
                            <div className="selectedList">
                                <div className="name">{fileList[0].name}</div>
                                <CloseRounded className="remove" onClick={onRemove} />
                            </div>
                        </div>
                }
                <div style={{ color: "grey", marginTop: "4px", fontSize: "14px" }}>
                    Upload Excel with header: <b>Policy_Number</b>
                </div>
            </div>
            <div className="actions">
                <Button type="primary" onClick={handelClick} disabled={disabled}>
                    Submit
                </Button>
            </div>
        </Dragger>
    );
};

export default UploadMedia;
